// Volume smoothing ("night mode") for the players — the pure half.
//
// Big-budget mixes ride a huge dynamic range: whispered dialogue sits ~30 dB
// below the battle scenes, so watching one at a comfortable level means
// riding the volume knob all evening. The players run their audio through a
// Web Audio compressor + output trim + soft clipper to pull that range in;
// this module owns the numbers and the pre-flight rules, so they stay
// Vue-free and testable.

export const NORMALIZER_LEVELS = ["light", "strong"];
export const DEFAULT_NORMALIZER_LEVEL = "strong";

/**
 * Leave some scheduling headroom for TV hardware without asking for the
 * large, device-dependent buffer selected by the "playback" latency hint.
 * Audio and video share a media element, so that extra buffer is audible as
 * late dialogue. The compressor itself only adds about 6 ms of look-ahead.
 */
export function createNormalizerContext(AudioContextCtor) {
  const context = new AudioContextCtor({ latencyHint: 0.01 });
  // Hints are advisory. If the browser gives us an oversized buffer anyway,
  // try its low-latency mode before any media element is attached. An element
  // cannot be moved to another context once createMediaElementSource runs.
  if (!Number.isFinite(context.baseLatency) || context.baseLatency <= 0.04) {
    return context;
  }

  const closeUnused = (unused) => {
    try { Promise.resolve(unused.close()).catch(() => {}); } catch { /* already closed */ }
  };
  let interactive;
  try {
    interactive = new AudioContextCtor({ latencyHint: "interactive" });
  } catch {
    return context;
  }
  if (Number.isFinite(interactive.baseLatency) && interactive.baseLatency < context.baseLatency) {
    closeUnused(context);
    return interactive;
  }
  closeUnused(interactive);
  return context;
}

/** Coerce a stored/URL-supplied level into a known one. */
export function sanitizeNormalizerLevel(value) {
  return NORMALIZER_LEVELS.includes(value) ? value : DEFAULT_NORMALIZER_LEVEL;
}

// Compressor curves. These are LEVELLER settings, not drum-bus settings: the
// thing being evened out is the gap between a quiet scene and a loud one,
// which plays out over seconds, so the envelope has to move over seconds too.
// A fast attack with a heavy ratio tracks individual syllables instead, and
// the whole mix audibly breathes around every transient.
//
// The threshold sits just under dialogue level rather than down in the noise
// floor, so quiet passages pass through untouched and only the loud ones get
// pulled down.
//
// There is deliberately NO makeup gain here. DynamicsCompressorNode applies
// the spec's own fixed makeup stage (1/compressorCurve(1.0)), so the node
// already hands back "peaks pulled down, everything else lifted" — light by
// ~3.6 dB, strong by ~11 dB. Stacking a second makeup gain on top of that is
// what pushed the output past full scale and kept the limiter permanently
// engaged. `fullScaleOutDb` is where a 0 dBFS input actually lands with these
// settings, measured through an OfflineAudioContext in Chrome.
const CURVES = {
  light: {
    threshold: -22,
    knee: 20,
    ratio: 2.5,
    attack: 0.04,
    release: 0.6,
    fullScaleOutDb: -2.0,
  },
  strong: {
    threshold: -26,
    knee: 18,
    ratio: 6,
    attack: 0.025,
    release: 0.4,
    fullScaleOutDb: -4.9,
  },
};

// Where a full-scale input should land after the trim. Leaving the last
// couple of dB unused keeps peaks clear of the soft clipper, and levelling
// both presets to the same ceiling means switching between them changes how
// even the mix is, not how loud it is.
export const TARGET_PEAK_DB = -2;

// Where the output soft clipper starts bending, in linear amplitude (~-3 dBFS).
export const SOFT_CLIP_KNEE = 0.7;

/**
 * Compressor settings for a level, plus the output trim that lands its peaks
 * on TARGET_PEAK_DB. Unknown level → the default.
 */
export function compressorParams(level) {
  const { fullScaleOutDb, ...curve } = CURVES[sanitizeNormalizerLevel(level)];
  return {
    ...curve,
    fullScaleOutDb,
    outputTrim: 10 ** ((TARGET_PEAK_DB - fullScaleOutDb) / 20),
  };
}

/**
 * Waveshaper transfer curve for the output safety net: identity below the
 * knee, asymptotic to full scale above it.
 *
 * This replaces what used to be a second DynamicsCompressor acting as a
 * limiter. A limiter that engages on ordinary peaks ducks the whole mix for
 * its release time, which is the pumping the compressor above is tuned to
 * avoid; a soft clipper can only ever round off the sample that overshot.
 * It is also a table lookup rather than a per-sample envelope follower, which
 * the Chromecast's audio thread notices.
 */
export function softClipCurve(points = 2048, knee = SOFT_CLIP_KNEE) {
  const curve = new Float32Array(points);
  const span = 1 - knee;
  for (let i = 0; i < points; i++) {
    const x = (i / (points - 1)) * 2 - 1;
    const a = Math.abs(x);
    const y = a <= knee ? a : knee + span * Math.tanh((a - knee) / span);
    curve[i] = x < 0 ? -y : y;
  }
  return curve;
}

// A MediaElementAudioSourceNode over cross-origin media that was fetched
// WITHOUT CORS outputs silence — permanently, for that load. So the element
// has to be re-fetched with crossorigin="anonymous" before the graph can be
// attached. blob: (MSE / hls.js) and same-origin URLs are already clean;
// anything that won't resolve at all is treated as needing it (a reload costs
// a buffering pause, guessing wrong costs the audio).
export function needsCrossOrigin(src, pageOrigin) {
  if (!src) return false;
  if (/^(blob|data|mediastream):/i.test(src)) return false;
  try {
    return new URL(src, pageOrigin).origin !== new URL(pageOrigin).origin;
  } catch {
    return true;
  }
}
